import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { getTileImages, Tile } from '../../types/tile.types';
import { TileService } from '../../services/tile.service';
import { TileImageEditorComponent } from '../tile-image-editor/tile-image-editor.component';
import { TileIconPickerComponent } from '../tile-icon-picker/tile-icon-picker.component';

@Component({
    selector: 'app-web-tile-editor',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, TileImageEditorComponent, TileIconPickerComponent],
    templateUrl: './web-tile-editor.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebTileEditorComponent {
    @Input() public scope: 'global' | 'tenant' | 'personal' = 'global';
    @Output() public saved = new EventEmitter<void>();
    public visible = false;
    public draft?: Tile;
    public saving = false;
    public loadingMetadata = false;
    public iconVisible = false;
    public confirmDelete = false;
    public error = '';
    private readonly tiles = inject(TileService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);

    public open(tile?: Tile): void {
        this.draft = tile ? { ...tile } : { _id: '', name: '', text: '', subtext: '', url: '', icon: 'ri-global-line', color: '#004f9f', openInNewTab: true, kind: 'link', permissions: [] };
        this.visible = true;
        this.error = '';
        this.confirmDelete = false;
        this.cdr.markForCheck();
    }

    public close(): void {
        if (this.saving) return;
        this.visible = false;
        this.draft = undefined;
        this.iconVisible = false;
        this.cdr.detectChanges();
    }

    public get validUrl(): boolean {
        try {
            const url = new URL(this.draft?.url ?? '');
            return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password;
        } catch { return false; }
    }

    public chooseIcon(icon: string): void {
        if (this.draft) {
            this.draft.icon = icon;
            Object.assign(this.draft, getTileImages(this.draft), { imageUrl: '', iconImageUrl: '', iconImagePrompt: '' });
        }
    }

    public async loadMetadata(): Promise<void> {
        if (!this.draft || !this.validUrl || this.loadingMetadata) return;
        const draft = this.draft;
        const url = draft.url;
        this.loadingMetadata = true;
        this.error = '';
        try {
            const metadata = await this.tiles.getWebsiteMetadata(url);
            if (this.draft !== draft || draft.url !== url) return;
            draft.name = metadata.name;
            draft.subtext = metadata.subtext;
            if (metadata.color) draft.color = metadata.color;
            if (metadata.imageUrl && this.scope !== 'personal') {
                Object.assign(draft, getTileImages(draft), { imageUrl: '', iconImageUrl: metadata.imageUrl, iconImagePrompt: '' });
            }
        } catch {
            if (this.draft === draft) this.error = 'Website-Daten konnten nicht übernommen werden. Die Angaben können manuell ergänzt werden.';
        } finally {
            this.loadingMetadata = false;
            if (!this.destroyRef.destroyed) this.cdr.detectChanges();
        }
    }

    public async save(): Promise<void> {
        if (!this.draft?.name.trim() || !this.validUrl || this.saving || this.loadingMetadata) return;
        this.saving = true;
        this.error = '';
        try {
            const response = await this.tiles.saveWebTile(this.draft, this.scope);
            if (this.destroyRef.destroyed) return;
            if (response.status !== 'ok') throw new Error(response.message);
            this.saving = false;
            this.close();
            this.saved.emit();
        } catch {
            this.error = 'Die Weblink-Kachel konnte nicht gespeichert werden. Bitte erneut versuchen.';
        } finally {
            this.saving = false;
            if (!this.destroyRef.destroyed) this.cdr.detectChanges();
        }
    }

    public async delete(): Promise<void> {
        if (!this.draft?._id || this.saving || this.loadingMetadata) return;
        if (!this.confirmDelete) { this.confirmDelete = true; return; }
        this.saving = true;
        this.error = '';
        try {
            const response = await this.tiles.deleteWebTile(this.draft._id, this.scope);
            if (this.destroyRef.destroyed) return;
            if (response.status !== 'ok') throw new Error(response.message);
            this.saving = false;
            this.close();
            this.saved.emit();
        } catch {
            this.error = 'Die Weblink-Kachel konnte nicht gelöscht werden.';
        } finally {
            this.saving = false;
            if (!this.destroyRef.destroyed) this.cdr.detectChanges();
        }
    }
}
